import * as React from "react";
import styled, { css } from "styled-components";
import { select, ColorType } from "@hubper/theme";

import { getDate } from "date-fns";

type BaseProps = {
  active: boolean;
  inRange: boolean;
  colorType: ColorType;
  size: string;
  first: boolean;
  last: boolean;
  pointer: boolean;
  disabled: boolean;
};

const innerHoverStyle = css`
  &:hover > * {
    border-radius: 50%;
    background-color: ${select.color(["grey", 1])};
  }
`;

const Base = styled.div<BaseProps>`
  transition: 0.2s;
  background-color: ${p =>
    p.inRange ? select.color([p.colorType, 1], p) : "transparent"};
  border-top-left-radius: ${p => (p.active && p.first ? "50%" : "0px")};
  border-bottom-left-radius: ${p => (p.active && p.first ? "50%" : "0px")};
  border-top-right-radius: ${p => (p.active && p.last ? "50%" : "0px")};
  border-bottom-right-radius: ${p => (p.active && p.last ? "50%" : "0px")};
  overflow: hidden;
  height: ${p => p.size};
  width: ${p => p.size};
  cursor: ${p => (p.pointer ? "pointer" : "default")};
  opacity: ${p => (p.disabled ? 0.8 : 1)};

  ${p => !p.disabled && !p.active && !p.inRange && innerHoverStyle}
`;

type InnerProps = {
  colorType: ColorType;
  active: boolean;
  inRange: boolean;
  fontSize: string;
  disabled: boolean;
  dateIsOutsideCurrentMonth: boolean;
};

const Inner = styled.div<InnerProps>`
  transition: 0.2s;
  transition-property: background-color, color;
  background-color: ${p =>
    p.active ? select.color([p.colorType, 5], p) : "transparent"};
  color: ${p =>
    p.active
      ? "white "
      : select.color(
          ["grey", p.disabled ? 2 : p.dateIsOutsideCurrentMonth ? 4 : 8],
          p
        )};
  border-radius: ${p => (!p.inRange || p.active ? "50%" : "0%")};
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: ${select.font("Lato")};
  font-size: ${p => p.fontSize};
  overflow: hidden;
`;

type Props = {
  active?: boolean;
  first?: boolean;
  last?: boolean;
  date: Date | null;
  onClick?: (date: Date) => void;
  inRange?: boolean;
  size?: string;
  fontSize?: string;
  colorType?: ColorType;
  value?: string;
  style?: React.CSSProperties;
  className?: string;
  disabled?: boolean;
  dateIsOutsideCurrentMonth?: boolean;
};

class Cell extends React.Component<Props> {
  handleClick = () => {
    const { date, onClick, disabled } = this.props;
    if (onClick && !disabled) {
      onClick(date!);
    }
  };

  render() {
    const {
      active,
      first,
      last,
      date,
      inRange,
      size = "32px",
      fontSize = "16px",
      colorType = "primary",
      value,
      onClick,
      style,
      className,
      disabled,
      dateIsOutsideCurrentMonth
    } = this.props;

    return (
      <Base
        active={active!}
        inRange={inRange!}
        colorType={colorType!}
        size={size}
        first={first!}
        last={last!}
        pointer={Boolean(onClick) && !disabled}
        onClick={this.handleClick}
        className={className}
        disabled={disabled!}
      >
        <Inner
          style={style}
          active={active!}
          inRange={inRange!}
          colorType={colorType}
          fontSize={fontSize}
          disabled={disabled!}
          dateIsOutsideCurrentMonth={dateIsOutsideCurrentMonth!}
        >
          {value || getDate(date!)}
        </Inner>
      </Base>
    );
  }
}

export default Cell;
